// pitchero/src/components/LoginPage.js - Centered design with two-step signup
// UPDATED: Added 3-hour session timeout support
import React, { useState, useEffect } from "react";
import { auth, db } from "../utils/firebase";
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut } from "firebase/auth";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useNavigate } from "react-router-dom";
import LoginGif from "../assets/images/LoginpageGIF10.gif";

// Event tracking for behavior analytics
import { trackAction, ACTIONS } from '../utils/useEventTracker';

// Import PDF documents
import PrivacyPolicyPDF from "../assets/Pitchallo-Privacy-Policy.pdf";
import TermsAndConditionsPDF from "../assets/Pitchallo-Terms-and-Conditions.pdf";

// Initialize Cloud Functions
const functions = getFunctions();

// =============================================================================
// SESSION MANAGEMENT - For 3-hour auto-logout feature
// =============================================================================
const SESSION_START_KEY = 'pitchallo_session_start';

const startSession = () => {
  const now = Date.now();
  localStorage.setItem(SESSION_START_KEY, now.toString());
  console.log('🔐 Session started at:', new Date(now).toLocaleString());
};

const clearSession = () => {
  localStorage.removeItem(SESSION_START_KEY);
};
// =============================================================================

function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLogin, setIsLogin] = useState(false); // Default to Sign Up view
  const [signupStep, setSignupStep] = useState(1); // Step 1: Email/Password, Step 2: Club details
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(false); // Loading state for form submissions
  const [showResendVerification, setShowResendVerification] = useState(false);
  const [showExistingMemberModal, setShowExistingMemberModal] = useState(false);
  const [existingMemberClubId, setExistingMemberClubId] = useState("");
  const navigate = useNavigate();
  
  // Club-related state for signup
  const [clubName, setClubName] = useState("");
  const [selectedClubId, setSelectedClubId] = useState("");
  const [isNewClub, setIsNewClub] = useState(true);

  // Contact information state for new club creators
  const [firstName, setFirstName] = useState("");
  const [surname, setSurname] = useState("");
  const [mobile, setMobile] = useState("");

  // Clear any existing session when landing on login page
  useEffect(() => {
    clearSession();
  }, []);

  // Check for stored error messages on component mount
  useEffect(() => {
    const storedError = sessionStorage.getItem('loginError');
    if (storedError) {
      setError(storedError);
      sessionStorage.removeItem('loginError');
    }
  }, []);

  // Format Australian mobile number as user types
  const formatMobileNumber = (value) => {
    const digits = value.replace(/\D/g, '');
    if (digits.length <= 4) {
      return digits;
    } else if (digits.length <= 7) {
      return `${digits.slice(0, 4)} ${digits.slice(4)}`;
    } else {
      return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7, 10)}`;
    }
  };

  const handleMobileChange = (e) => {
    const formatted = formatMobileNumber(e.target.value);
    setMobile(formatted);
  };

  // Check if user already exists in another club
  const checkExistingMembership = async (userEmail, targetClubId = null) => {
    try {
      console.log('🔍 Checking for existing membership for:', userEmail);
      
      // Import query and collection functions
      const { collection, query, where, getDocs } = await import('firebase/firestore');
      
      // Query for any user document with this email
      const usersRef = collection(db, 'users');
      const q = query(usersRef, where('email', '==', userEmail));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const existingUserDoc = querySnapshot.docs[0];
        const existingUserData = existingUserDoc.data();
        const existingClubId = existingUserData.clubId;
        const existingStatus = existingUserData.status;
        
        console.log('📋 Existing user found:', {
          clubId: existingClubId,
          status: existingStatus,
          targetClubId: targetClubId
        });
        
        // Check if they're trying to join a DIFFERENT club
        if (targetClubId && existingClubId !== targetClubId) {
          console.log('⚠️ User attempting to join different club!');
          return {
            exists: true,
            clubId: existingClubId,
            status: existingStatus
          };
        }
        
        // If no target club specified (login), just return the data
        if (!targetClubId) {
          return {
            exists: true,
            clubId: existingClubId,
            status: existingStatus
          };
        }
      }
      
      console.log('✅ No existing membership conflict');
      return { exists: false };
      
    } catch (error) {
      console.error('❌ Error checking existing membership:', error);
      // On error, allow the process to continue (fail open)
      return { exists: false };
    }
  };

  // Handle moving to step 2 of signup
  const handleContinueToStep2 = async (e) => {
    e.preventDefault();
    setError("");
    
    // Validate email and password
    if (!email || !password) {
      setError("Please enter both email and password");
      return;
    }
    
    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    
    // Check if this email already exists in any club
    const membershipCheck = await checkExistingMembership(email);
    if (membershipCheck.exists) {
      console.log('⚠️ Email already registered to club:', membershipCheck.clubId);
      setExistingMemberClubId(membershipCheck.clubId);
      setShowExistingMemberModal(true);
      return;
    }
    
    // Move to step 2
    setSignupStep(2);
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setError("");
    setSuccessMessage("");
    setShowResendVerification(false);
    setLoading(true);
    
    console.log('🔐 Attempting login...');

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      console.log('✅ Login successful:', userCredential.user.uid);
      
      // Check if user document exists and get their clubId
      const userDoc = await getDoc(doc(db, 'users', userCredential.user.uid));
      if (!userDoc.exists()) {
        console.log('⚠️ User document not found in Firestore');
        await signOut(auth);
        setError('Account information not found. Please contact support.');
        setLoading(false);
        return;
      }
      
      const userData = userDoc.data();
      const userClubId = userData.clubId;
      console.log('👤 User belongs to club:', userClubId);
      
      // Reload user to get fresh email verification status
      await userCredential.user.reload();
      const refreshedUser = auth.currentUser;
      
      if (!refreshedUser.emailVerified) {
        console.log('⚠️ Email not verified, trying re-login...');
        await signOut(auth);
        const recheckCredential = await signInWithEmailAndPassword(auth, email, password);
        await recheckCredential.user.reload();
        const finalUser = auth.currentUser;
        
        if (!finalUser.emailVerified) {
          console.log('❌ Email still not verified - blocking login');
          await signOut(auth);
          setError('Please verify your email address before logging in. Check your inbox (including spam folder) for the verification link.');
          setShowResendVerification(true);
          return;
        }
      }
      
      // Check if user is deactivated
      if (userData.status === 'deactivated') {
        console.log('❌ User is deactivated');
        sessionStorage.setItem('loginError', 'Your account has been deactivated. Please contact your club administrator for assistance.');
        await signOut(auth);
        setError('Your account has been deactivated. Please contact your club administrator for assistance.');
        setLoading(false);
        return;
      }
      
      // Update last login timestamp
      await setDoc(doc(db, 'users', userCredential.user.uid), {
        ...userData,
        lastLogin: new Date().toISOString()
      }, { merge: true });
      
      // Start 3-hour session timer
      startSession();
      
      // Track successful login for behavior analytics
      trackAction(ACTIONS.LOGIN, { clubId: userClubId, method: 'email' });
      
      console.log('✅ Login complete, navigating to menu...');
      navigate("/menu");
    } catch (error) {
      console.error("❌ Login error:", error);
      
      if (error.code === 'auth/invalid-credential') {
        setError('Invalid email or password. Please check your credentials and try again.');
      } else if (error.code === 'auth/user-not-found') {
        setError('No account found with this email. Please sign up first.');
      } else if (error.code === 'auth/wrong-password') {
        setError('Incorrect password. Please try again.');
      } else if (error.code === 'auth/too-many-requests') {
        setError('Too many attempts. Please try again later.');
      } else {
        setError(error.message || "An error occurred. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setError("");
    setSuccessMessage("");
    setLoading(true);
    
    console.log('📝 Starting signup process...');

    try {
      // Validate inputs
      if (!isNewClub && !selectedClubId.trim()) {
        setError("Please enter a club ID");
        setLoading(false);
        return;
      }
      if (!isNewClub && selectedClubId.trim().length !== 6) {
        setError("Club ID must be exactly 6 characters");
        setLoading(false);
        return;
      }
      if (isNewClub && !clubName.trim()) {
        setError("Please enter a club name");
        setLoading(false);
        return;
      }

      // Validate contact information for all users
      if (!firstName.trim()) {
        setError("Please enter your first name");
        setLoading(false);
        return;
      }
      if (!surname.trim()) {
        setError("Please enter your surname");
        setLoading(false);
        return;
      }
      if (!mobile.trim()) {
        setError("Please enter your mobile number");
        setLoading(false);
        return;
      }

      const mobileDigits = mobile.replace(/\D/g, '');
      if (!mobileDigits.startsWith('04') || mobileDigits.length !== 10) {
        setError("Please enter a valid Australian mobile number (04XX XXX XXX)");
        setLoading(false);
        return;
      }

      // If joining existing club, validate it exists
      if (!isNewClub) {
        console.log('🔍 Validating existing club...');
        const clubDoc = await getDoc(doc(db, 'clubs', selectedClubId.trim().toUpperCase()));
        if (!clubDoc.exists()) {
          setError("Invalid club ID. Please check with your club administrator.");
          setLoading(false);
          return;
        }
        
        // Check if user already exists in a DIFFERENT club
        const membershipCheck = await checkExistingMembership(email, selectedClubId.trim().toUpperCase());
        if (membershipCheck.exists && membershipCheck.clubId !== selectedClubId.trim().toUpperCase()) {
          console.log('⚠️ User already member of different club:', membershipCheck.clubId);
          setExistingMemberClubId(membershipCheck.clubId);
          setShowExistingMemberModal(true);
          setLoading(false);
          return;
        }
      }

      console.log('✅ All validations passed, creating account...');
      
      // Create Firebase Auth account with email/password
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      console.log('✅ User created:', userCredential.user.uid);

      // NOTE: Verification email is sent automatically by cloud function (sendVerificationEmail)
      console.log('✅ Verification email will be sent by cloud function');
      
      // Sign out immediately - user must verify email
      console.log('🔒 Signing out immediately - user must verify email...');
      await signOut(auth);
      console.log('✅ User signed out');
      
      try {
        if (isNewClub) {
          console.log('🏢 Creating new club...');
          
          const clubId = Math.random().toString(36).substring(2, 8).toUpperCase();
          const mobileDigits = mobile.replace(/\D/g, '');
          
          // Calculate trial end date (7 days from now)
          const now = new Date();
          const trialEndsAt = new Date(now);
          trialEndsAt.setDate(trialEndsAt.getDate() + 7);

          const cloudFunctionData = { 
            uid: userCredential.user.uid,
            email: email,
            clubName: clubName.trim(),
            clubId: clubId,
            firstName: firstName.trim(),
            surname: surname.trim(),
            mobile: mobileDigits,
            // Trial fields - Give premium access during trial
            createdAt: now.toISOString(),
            trialEndsAt: trialEndsAt.toISOString(),
            subscriptionStatus: 'trial',
            subscriptionTier: 'premium' // Give premium access during trial
          };
          
          console.log('📤 Sending to createClubAndUser:', cloudFunctionData);
          
          const createClubAndUser = httpsCallable(functions, 'createClubAndUser');
          const result = await createClubAndUser(cloudFunctionData);
          
          console.log('✅ Cloud Function response:', result.data);
          
          // Track successful signup for behavior analytics
          trackAction(ACTIONS.SIGNUP, { clubId: result.data.clubId, isNewClub: true });
          
          setSuccessMessage(`Account created successfully! A verification email has been sent to ${email}. Please verify your email before logging in. Your club ID is: ${result.data.clubId}`);
          setShowResendVerification(true);
          setTimeout(() => {
            setIsLogin(true);
            setSignupStep(1);
            setSuccessMessage("");
          }, 8000);
        } else {
          console.log('👥 Joining existing club...');
          
          // Calculate trial end date (7 days from now)
          const now = new Date();
          const trialEndsAt = new Date(now);
          trialEndsAt.setDate(trialEndsAt.getDate() + 7);
          const mobileDigits = mobile.replace(/\D/g, '');
          
          const cloudFunctionData = { 
            uid: userCredential.user.uid,
            email: email,
            clubId: selectedClubId.trim().toUpperCase(),
            firstName: firstName.trim(),
            surname: surname.trim(),
            mobile: mobileDigits,
            // Trial fields - Give premium access during trial
            createdAt: now.toISOString(),
            trialEndsAt: trialEndsAt.toISOString(),
            subscriptionStatus: 'trial',
            subscriptionTier: 'premium' // Give premium access during trial
          };
          
          console.log('📤 Sending to joinClub:', cloudFunctionData);
          
          const joinClub = httpsCallable(functions, 'joinClub');
          const result = await joinClub(cloudFunctionData);
          
          console.log('✅ Cloud Function response:', result.data);
          
          // Track successful signup for behavior analytics
          trackAction(ACTIONS.SIGNUP, { clubId: selectedClubId.trim().toUpperCase(), isNewClub: false });
          
          setSuccessMessage(`Account created successfully! A verification email has been sent to ${email}. Please verify your email before logging in.`);
          setShowResendVerification(true);
          setTimeout(() => {
            setIsLogin(true);
            setSignupStep(1);
            setSuccessMessage("");
          }, 8000);
        }
        
      } catch (cloudFunctionError) {
        console.error('❌ Cloud Function error:', cloudFunctionError);
        console.error('Error details:', cloudFunctionError.message);
        
        // If Cloud Function fails, try to clean up the auth account
        try {
          await signInWithEmailAndPassword(auth, email, password);
          const user = auth.currentUser;
          if (user) {
            await user.delete();
            console.log('🧹 Cleaned up auth account after error');
          }
        } catch (deleteError) {
          console.error('Failed to delete auth account:', deleteError);
        }
        
        throw new Error(cloudFunctionError.message || 'Failed to complete signup');
      }
    } catch (error) {
      console.error("❌ Signup error:", error);
      
      if (error.code === 'auth/email-already-in-use') {
        setError('An account with this email already exists. Please login instead.');
      } else if (error.code === 'auth/weak-password') {
        setError('Password is too weak. Please use at least 6 characters.');
      } else if (error.code === 'auth/invalid-email') {
        setError('Invalid email address format.');
      } else {
        setError(error.message || "An error occurred. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    try {
      setError("");
      
      if (!email) {
        setError("Please enter your email address to resend verification");
        return;
      }
      
      // Call cloud function to resend verification email
      const resendVerification = httpsCallable(functions, 'resendVerificationEmail');
      await resendVerification({ email: email });
      
      setSuccessMessage("Verification email resent! Please check your inbox.");
      setShowResendVerification(false);
    } catch (error) {
      console.error("Error resending verification:", error);
      setError("Failed to resend verification email. Please try again.");
    }
  };

  return (
    <div style={{ 
      minHeight: "100vh",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      backgroundColor: "#243665",
      padding: "40px 20px"
    }}>
      {/* Pitchallo GIF at top center */}
      <div style={{ 
        marginBottom: "32px",
        textAlign: "center"
      }}>
        <img 
          src={LoginGif} 
          alt="Pitchallo" 
          style={{ 
            maxWidth: "300px",
            width: "100%",
            height: "auto",
            borderRadius: "12px"
          }} 
        />
      </div>

      <div style={{
        width: "100%",
        maxWidth: "460px",
        backgroundColor: "white",
        padding: "40px",
        borderRadius: "12px",
        boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.3), 0 8px 10px -6px rgba(0, 0, 0, 0.3)"
      }}>
        {/* Header */}
        <div style={{ marginBottom: "32px" }}>
          <h1 style={{ 
            margin: "0 0 8px 0",
            fontSize: "32px",
            fontWeight: "700",
            color: "#111827",
            lineHeight: "1.2"
          }}>
            {isLogin ? "Log in to your account" : "Start your free trial"}
          </h1>
          {!isLogin && (
            <p style={{ 
              margin: "0",
              fontSize: "16px",
              color: "#6b7280",
              lineHeight: "1.5"
            }}>
              Get full access to our Premium subcription for 7 days, absolutely free.
            </p>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div style={{ 
            padding: "12px 16px",
            backgroundColor: "#fef2f2",
            border: "1px solid #fecaca",
            borderRadius: "8px",
            marginBottom: "20px",
            color: "#dc2626",
            fontSize: "14px",
            lineHeight: "1.5"
          }}>
            {error}
          </div>
        )}

        {/* Success Message */}
        {successMessage && (
          <div style={{ 
            padding: "12px 16px",
            backgroundColor: "#f0fdf4",
            border: "1px solid #bbf7d0",
            borderRadius: "8px",
            marginBottom: "20px",
            color: "#166534",
            fontSize: "14px",
            lineHeight: "1.5"
          }}>
            {successMessage}
          </div>
        )}

        {/* Resend Verification Button */}
        {showResendVerification && (
          <div style={{ marginBottom: "20px" }}>
            <button
              onClick={handleResendVerification}
              style={{
                width: "100%",
                padding: "12px",
                backgroundColor: "#f3f4f6",
                color: "#374151",
                border: "1px solid #d1d5db",
                borderRadius: "8px",
                fontSize: "14px",
                fontWeight: "500",
                cursor: "pointer"
              }}
            >
              Resend Verification Email
            </button>
          </div>
        )}

        {/* Login Form */}
        {isLogin ? (
          <form onSubmit={handleLogin}>
            <div style={{ marginBottom: "16px" }}>
              <label style={{ 
                display: "block",
                fontSize: "14px",
                fontWeight: "500",
                color: "#374151",
                marginBottom: "6px"
              }}>
                Email
              </label>
              <input
                type="email"
                placeholder="your.email@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                  autoComplete="email"
                style={{ 
                  width: "100%",
                  padding: "12px 14px",
                  fontSize: "16px",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  boxSizing: "border-box",
                  outline: "none"
                }}
              />
            </div>

            <div style={{ marginBottom: "24px" }}>
              <label style={{ 
                display: "block",
                fontSize: "14px",
                fontWeight: "500",
                color: "#374151",
                marginBottom: "6px"
              }}>
                Password
              </label>
              <input
                type="password"
                placeholder="••••••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                  autoComplete="current-password"
                style={{ 
                  width: "100%",
                  padding: "12px 14px",
                  fontSize: "16px",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  boxSizing: "border-box",
                  outline: "none"
                }}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{ 
                width: "100%",
                padding: "14px",
                fontSize: "16px",
                fontWeight: "600",
                color: "white",
                backgroundColor: loading ? "#6b7280" : "#243665",
                border: "none",
                borderRadius: "8px",
                cursor: loading ? "not-allowed" : "pointer",
                marginBottom: "24px",
                opacity: loading ? 0.6 : 1,
                transition: "all 0.2s"
              }}
            >
              {loading ? "Logging in..." : "Log in"}
            </button>
          </form>
        ) : signupStep === 1 ? (
          /* Step 1: Email and Password */
          <form onSubmit={handleContinueToStep2}>
            <div style={{ marginBottom: "16px" }}>
              <label style={{ 
                display: "block",
                fontSize: "14px",
                fontWeight: "500",
                color: "#374151",
                marginBottom: "6px"
              }}>
                Email
              </label>
              <input
                type="email"
                placeholder="your.email@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                style={{ 
                  width: "100%",
                  padding: "12px 14px",
                  fontSize: "16px",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  boxSizing: "border-box",
                  outline: "none"
                }}
              />
            </div>

            <div style={{ marginBottom: "24px" }}>
              <label style={{ 
                display: "block",
                fontSize: "14px",
                fontWeight: "500",
                color: "#374151",
                marginBottom: "6px"
              }}>
                Password
              </label>
              <input
                type="password"
                placeholder="••••••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength="6"
                style={{ 
                  width: "100%",
                  padding: "12px 14px",
                  fontSize: "16px",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  boxSizing: "border-box",
                  outline: "none"
                }}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              style={{ 
                width: "100%",
                padding: "14px",
                fontSize: "16px",
                fontWeight: "600",
                color: "white",
                backgroundColor: loading ? "#6b7280" : "#243665",
                border: "none",
                borderRadius: "8px",
                cursor: loading ? "not-allowed" : "pointer",
                marginBottom: "24px",
                opacity: loading ? 0.6 : 1,
                transition: "all 0.2s"
              }}
            >
              {loading ? "Please wait..." : "Create Pitchallo account"}
            </button>
          </form>
        ) : (
          /* Step 2: Club Details */
          <form onSubmit={handleSignup}>
            {/* Email and Password fields - prepopulated */}
            <div style={{ marginBottom: "16px" }}>
              <label style={{ 
                display: "block",
                fontSize: "14px",
                fontWeight: "500",
                color: "#374151",
                marginBottom: "6px"
              }}>
                Email
              </label>
              <input
                type="email"
                placeholder="your.email@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                disabled
                style={{ 
                  width: "100%",
                  padding: "12px 14px",
                  fontSize: "16px",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  boxSizing: "border-box",
                  outline: "none",
                  backgroundColor: "#f9fafb",
                  cursor: "not-allowed",
                  color: "#6b7280"
                }}
              />
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label style={{ 
                display: "block",
                fontSize: "14px",
                fontWeight: "500",
                color: "#374151",
                marginBottom: "6px"
              }}>
                Password
              </label>
              <input
                type="password"
                placeholder="••••••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength="6"
                autoComplete="new-password"
                disabled
                style={{ 
                  width: "100%",
                  padding: "12px 14px",
                  fontSize: "16px",
                  border: "1px solid #d1d5db",
                  borderRadius: "8px",
                  boxSizing: "border-box",
                  outline: "none",
                  backgroundColor: "#f9fafb",
                  cursor: "not-allowed",
                  color: "#6b7280"
                }}
              />
            </div>

            {/* Club Setup */}
            <div style={{ 
              marginBottom: "16px",
              padding: "16px",
              backgroundColor: "#f9fafb",
              border: "1px solid #e5e7eb",
              borderRadius: "8px"
            }}>
              <label style={{ 
                display: "block",
                fontSize: "14px",
                fontWeight: "600",
                color: "#111827",
                marginBottom: "12px"
              }}>
                Club Setup
              </label>

              <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginBottom: "12px" }}>
                <label style={{ 
                  display: "flex", 
                  alignItems: "center", 
                  gap: "8px",
                  cursor: "pointer",
                  padding: "8px",
                  borderRadius: "6px",
                  backgroundColor: isNewClub ? "#eff6ff" : "transparent"
                }}>
                  <input
                    type="radio"
                    name="clubOption"
                    checked={isNewClub}
                    onChange={() => setIsNewClub(true)}
                    style={{
                      width: "16px",
                      height: "16px",
                      cursor: "pointer"
                    }}
                  />
                  <span style={{ fontSize: "14px" }}>Create new club (7-day free trial)</span>
                </label>

                <label style={{ 
                  display: "flex", 
                  alignItems: "center", 
                  gap: "8px",
                  cursor: "pointer",
                  padding: "8px",
                  borderRadius: "6px",
                  backgroundColor: !isNewClub ? "#eff6ff" : "transparent"
                }}>
                  <input
                    type="radio"
                    name="clubOption"
                    checked={!isNewClub}
                    onChange={() => setIsNewClub(false)}
                    style={{
                      width: "16px",
                      height: "16px",
                      cursor: "pointer"
                    }}
                  />
                  <span style={{ fontSize: "14px" }}>Join existing club (requires club ID)</span>
                </label>
              </div>

              {isNewClub ? (
                <input
                  type="text"
                  placeholder="Enter club name (e.g., Manchester United FC)"
                  value={clubName}
                  onChange={(e) => setClubName(e.target.value)}
                  required
                  autoComplete="organization"
                  style={{ 
                    width: "100%",
                    padding: "12px 14px",
                    fontSize: "16px",
                    border: "1px solid #d1d5db",
                    borderRadius: "8px",
                    boxSizing: "border-box",
                    outline: "none"
                  }}
                />
              ) : (
                <div>
                  <input
                    type="text"
                    placeholder="Enter 6-character club ID"
                    value={selectedClubId}
                    onChange={(e) => setSelectedClubId(e.target.value.toUpperCase())}
                    required={!isNewClub}
                    maxLength="6"
                    autoComplete="off"
                    style={{ 
                      width: "100%",
                      padding: "12px 14px",
                      fontSize: "16px",
                      border: "1px solid #d1d5db",
                      borderRadius: "8px",
                      fontFamily: "monospace",
                      letterSpacing: "1px",
                      boxSizing: "border-box",
                      outline: "none"
                    }}
                  />
                  <p style={{ fontSize: "12px", color: "#6b7280", marginTop: "6px", marginBottom: "0" }}>
                    Get this ID from your club administrator
                  </p>
                </div>
              )}
            </div>

            {/* Contact Information - For all users */}
            <div style={{ 
              marginBottom: "24px",
              padding: "16px",
              backgroundColor: "#f9fafb",
              border: "1px solid #e5e7eb",
              borderRadius: "8px"
            }}>
              <label style={{ 
                display: "block",
                fontSize: "14px",
                fontWeight: "600",
                color: "#111827",
                marginBottom: "12px"
              }}>
                Your Details
              </label>

                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginBottom: "12px" }}>
                  <div>
                    <label style={{ 
                      display: "block",
                      fontSize: "13px",
                      fontWeight: "500",
                      color: "#374151",
                      marginBottom: "6px"
                    }}>
                      First Name
                    </label>
                    <input
                      type="text"
                      placeholder="John"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      required
                      autoComplete="given-name"
                      style={{ 
                        width: "100%",
                        padding: "12px 14px",
                        fontSize: "16px",
                        border: "1px solid #d1d5db",
                        borderRadius: "8px",
                        boxSizing: "border-box",
                        outline: "none"
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ 
                      display: "block",
                      fontSize: "13px",
                      fontWeight: "500",
                      color: "#374151",
                      marginBottom: "6px"
                    }}>
                      Surname
                    </label>
                    <input
                      type="text"
                      placeholder="Smith"
                      value={surname}
                      onChange={(e) => setSurname(e.target.value)}
                      required
                      autoComplete="family-name"
                      style={{ 
                        width: "100%",
                        padding: "12px 14px",
                        fontSize: "16px",
                        border: "1px solid #d1d5db",
                        borderRadius: "8px",
                        boxSizing: "border-box",
                        outline: "none"
                      }}
                    />
                  </div>
                </div>

                <div>
                  <label style={{ 
                    display: "block",
                    fontSize: "13px",
                    fontWeight: "500",
                    color: "#374151",
                    marginBottom: "6px"
                  }}>
                    Mobile Number
                  </label>
                  <input
                    type="tel"
                    placeholder="0412 345 678"
                    value={mobile}
                    onChange={handleMobileChange}
                    required
                    autoComplete="tel"
                    maxLength="12"
                    style={{ 
                      width: "100%",
                      padding: "12px 14px",
                      fontSize: "16px",
                      border: "1px solid #d1d5db",
                      borderRadius: "8px",
                      boxSizing: "border-box",
                      outline: "none"
                    }}
                  />
                  <p style={{ fontSize: "12px", color: "#6b7280", marginTop: "6px", marginBottom: "0" }}>
                    We’ll only use your number for important updates about your club. We’ll never share it with anyone else. 
                  </p>
                </div>
              </div>

            <button
              type="submit"
              disabled={loading || successMessage}
              style={{ 
                width: "100%",
                padding: "14px",
                fontSize: "16px",
                fontWeight: "600",
                color: "white",
                backgroundColor: (loading || successMessage) ? "#6b7280" : "#243665",
                border: "none",
                borderRadius: "8px",
                cursor: (loading || successMessage) ? "not-allowed" : "pointer",
                marginBottom: "16px",
                opacity: (loading || successMessage) ? 0.6 : 1,
                transition: "all 0.2s"
              }}
            >
              {loading ? "Creating account..." : successMessage ? "Check your email to verify" : "Create Pitchallo account"}
            </button>

            <button
              type="button"
              onClick={() => setSignupStep(1)}
              disabled={loading || successMessage}
              style={{ 
                width: "100%",
                padding: "12px",
                fontSize: "14px",
                fontWeight: "500",
                color: (loading || successMessage) ? "#9ca3af" : "#374151",
                backgroundColor: "white",
                border: "1px solid #d1d5db",
                borderRadius: "8px",
                cursor: (loading || successMessage) ? "not-allowed" : "pointer",
                marginBottom: "24px",
                opacity: (loading || successMessage) ? 0.5 : 1
              }}
            >
              ← Back
            </button>
          </form>
        )}

        {/* Existing Users Login Button - Only show on signup step 1 */}
        {!isLogin && signupStep === 1 && (
          <button
            onClick={() => {
              setIsLogin(true);
              setError("");
              setSuccessMessage("");
              setShowResendVerification(false);
            }}
            style={{
              width: "100%",
              padding: "12px",
              fontSize: "15px",
              fontWeight: "500",
              color: "#374151",
              backgroundColor: "white",
              border: "1px solid #d1d5db",
              borderRadius: "8px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: "24px"
            }}
          >
            Existing users Log in
          </button>
        )}

        {/* Footer Links - Only show on signup step 1 */}
        {!isLogin && signupStep === 1 && (
              <div style={{ 
                marginTop: "24px",
                paddingTop: "24px",
                borderTop: "1px solid #e5e7eb"
              }}>
                <p style={{ 
                  fontSize: "12px",
                  color: "#6b7280",
                  textAlign: "center",
                  lineHeight: "1.6",
                  margin: "0"
                }}>
                  By proceeding, you agree to the{" "}
                  <a 
                    href={TermsAndConditionsPDF} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    style={{ color: "#3b82f6", textDecoration: "none" }}
                  >
                    Terms and Conditions
                  </a>
                  {" "}and{" "}
                  <a 
                    href={PrivacyPolicyPDF} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    style={{ color: "#3b82f6", textDecoration: "none" }}
                  >
                    Privacy Policy
                  </a>
                </p>
              </div>
            )}
      </div>

      {/* Existing Member Modal */}
      {showExistingMemberModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '32px',
            maxWidth: '500px',
            width: '100%',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'start',
              gap: '16px',
              marginBottom: '20px'
            }}>
              <div style={{
                flex: '0 0 auto',
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                backgroundColor: '#fee2e2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <span style={{ fontSize: '24px' }}>⚠️</span>
              </div>
              <div style={{ flex: 1 }}>
                <h3 style={{
                  fontSize: '20px',
                  fontWeight: '600',
                  color: '#1f2937',
                  margin: '0 0 12px 0'
                }}>
                  Already a Member
                </h3>
                <p style={{
                  fontSize: '15px',
                  color: '#6b7280',
                  margin: 0,
                  lineHeight: '1.6'
                }}>
                  This email address is already registered as an {existingMemberClubId ? 'active or deactivated' : ''} member of {existingMemberClubId ? `club "${existingMemberClubId}"` : 'another club'}.
                </p>
                <p style={{
                  fontSize: '15px',
                  color: '#6b7280',
                  margin: '12px 0 0 0',
                  lineHeight: '1.6'
                }}>
                  You cannot join or create a different club with this email address.
                </p>
                <div style={{
                  marginTop: '16px',
                  padding: '16px',
                  backgroundColor: '#f0fdf4',
                  border: '1px solid #86efac',
                  borderRadius: '8px'
                }}>
                  <p style={{
                    fontSize: '14px',
                    color: '#15803d',
                    margin: '0 0 8px 0',
                    fontWeight: '600'
                  }}>
                    Need help?
                  </p>
                  <p style={{
                    fontSize: '14px',
                    color: '#15803d',
                    margin: 0,
                    lineHeight: '1.5'
                  }}>
                    If you believe this is an error or need to change your club membership, please contact:
                  </p>
                  <a 
                    href="mailto:pitchalloaustralia@gmail.com"
                    style={{
                      fontSize: '14px',
                      color: '#15803d',
                      fontWeight: '600',
                      textDecoration: 'none',
                      display: 'block',
                      marginTop: '8px'
                    }}
                  >
                    pitchalloaustralia@gmail.com
                  </a>
                </div>
              </div>
            </div>
            
            <div style={{
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end',
              marginTop: '24px'
            }}>
              <button
                onClick={() => {
                  setShowExistingMemberModal(false);
                  setExistingMemberClubId("");
                  // Reset form
                  setEmail("");
                  setPassword("");
                  setSelectedClubId("");
                  setSignupStep(1);
                }}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#3b82f6',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '15px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  color: 'white'
                }}
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LoginPage;
